import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Search, 
  Filter, 
  Lock, 
  History, 
  Terminal, 
  AlertTriangle,
  UserCheck,
  FileText
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { SecurityAuditLog } from '../types/admin';

export const AuditLogsView: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');

  const [logs] = useState<SecurityAuditLog[]>([
    {
      id: 'log_9901',
      adminUid: 'superadmin_master_001',
      adminEmail: 'oliversammytwist@gmail.com',
      adminRole: 'super_admin',
      action: 'ESCROW_FORCE_RELEASE',
      entityType: 'order',
      entityId: 'ord_escrow_001',
      details: 'Super Admin manual override: released ₦321,781 to vendor Tunde Bakare after confirming phone delivery clearance with buyer.',
      ipAddress: '102.89.23.14 (Lagos, NG)',
      timestamp: 'Today at 11:32:04 AM'
    },
    {
      id: 'log_9902',
      adminUid: 'superadmin_master_001',
      adminEmail: 'oliversammytwist@gmail.com',
      adminRole: 'super_admin',
      action: 'KYC_APPROVE_TIER2',
      entityType: 'kyc',
      entityId: 'usr_004_fatima',
      details: 'Approved National Identification Number (NIN: 5482 9102 3841). Awarded Silver Merchant badge and +20 trust score points.',
      ipAddress: '102.89.23.14 (Lagos, NG)',
      timestamp: 'Today at 10:15:18 AM'
    },
    {
      id: 'log_9903',
      adminUid: 'staff_mediator_002',
      adminEmail: 'mediator@reuseit.shop',
      adminRole: 'mediator',
      action: 'DISPUTE_VERDICT_SPLIT',
      entityType: 'dispute',
      entityId: 'dsp_case_002',
      details: 'Delivered 50/50 split payout on Order #ORD-9799 for minor leather scuff. ₦11,000 refunded to buyer; ₦10,113 net settled to vendor.',
      ipAddress: '197.210.55.90 (Abuja, NG)',
      timestamp: 'Yesterday at 03:40:12 PM'
    },
    {
      id: 'log_9904',
      adminUid: 'superadmin_master_001',
      adminEmail: 'oliversammytwist@gmail.com',
      adminRole: 'super_admin',
      action: 'ACCOUNT_HARD_DELETE_SCRUB',
      entityType: 'deleted_account',
      entityId: 'del_acc_901',
      details: 'Executed cryptographic purge of quarantined records for Adewale Balogun (originalUid: usr_del_ade_092) per verified Right-to-be-Forgotten request.',
      ipAddress: '102.89.23.14 (Lagos, NG)',
      timestamp: '2 days ago'
    },
    {
      id: 'log_9905',
      adminUid: 'superadmin_master_001',
      adminEmail: 'oliversammytwist@gmail.com',
      adminRole: 'super_admin',
      action: 'FCM_BROADCAST_DISPATCHED',
      entityType: 'broadcast',
      entityId: 'bc_001',
      details: 'Dispatched broadcast push campaign "Lagos Flash Thrift Drop" to 3,840 active subscriber device tokens.',
      ipAddress: '102.89.23.14 (Lagos, NG)',
      timestamp: '2 days ago'
    }
  ]);

  const filtered = logs.filter(l => {
    const matchSearch = l.adminEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        l.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        l.entityId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        l.details.toLowerCase().includes(searchQuery.toLowerCase());
    const matchEntity = entityFilter === 'all' || l.entityType === entityFilter;
    return matchSearch && matchEntity;
  });

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="p-6 rounded-3xl border transition-colors flex flex-col md:flex-row items-start md:items-center justify-between gap-4
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="flex items-start gap-4">
          <div className="p-3.5 rounded-2xl bg-brand-500/15 border border-brand-500/30 text-brand-400 shrink-0">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17]">
                Security Audit Logs & Immutable Action Trail
              </h3>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-brand-500/20 text-brand-300 border border-brand-500/30">
                WORM Compliant (Write Once, Read Many)
              </span>
            </div>
            <p className="mt-1 text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] max-w-3xl leading-relaxed">
              Every sensitive operation—including manual escrow fund releases, dispute rulings, user bans, KYC reviews, and hard account scrubs—is cryptographically recorded with operator identity, IP address, and timestamp.
            </p>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl border
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC]
      ">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search action, email, or entity ID..."
            className="w-full pl-9 pr-4 py-2 rounded-xl text-xs outline-none border transition-colors
              bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
              light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
              focus:border-brand-500"
          />
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <span className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">Entity:</span>
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="px-3 py-1.5 rounded-xl text-xs font-medium border outline-none
              bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
              light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
            "
          >
            <option value="all">All Operations</option>
            <option value="order">Escrow Orders</option>
            <option value="dispute">Dispute Tribunal</option>
            <option value="kyc">KYC Verifications</option>
            <option value="deleted_account">Deleted Account Scrubs</option>
            <option value="broadcast">Broadcasts</option>
          </select>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="rounded-3xl border overflow-hidden transition-colors
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider font-semibold">
                <th className="py-4 px-6">Timestamp & Admin</th>
                <th className="py-4 px-6">Action Type</th>
                <th className="py-4 px-6">Target Entity</th>
                <th className="py-4 px-6">Audit Details</th>
                <th className="py-4 px-6 text-right">Client IP / Session</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-[#EBF2EE]">
              {filtered.map((log) => (
                <tr key={log.id} className="hover:bg-[#1a382e]/50 dark:hover:bg-[#1a382e]/50 light:hover:bg-gray-50/80 transition-colors">
                  <td className="py-4 px-6">
                    <div className="font-bold text-white dark:text-white light:text-[#0D1C17]">{log.adminEmail}</div>
                    <div className="text-[11px] text-brand-400 font-medium capitalize">{log.adminRole.replace('_', ' ')}</div>
                    <div className="text-[10px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-0.5">{log.timestamp}</div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="font-mono text-[11px] font-bold px-2 py-0.5 rounded-lg bg-[#10231d] dark:bg-[#10231d] text-brand-400 border border-[#22493c]">
                      {log.action}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-[11px] uppercase font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] block">{log.entityType}</span>
                    <span className="font-mono text-xs font-semibold text-white dark:text-white light:text-[#0D1C17]">{log.entityId}</span>
                  </td>
                  <td className="py-4 px-6 max-w-md">
                    <p className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] leading-relaxed">
                      {log.details}
                    </p>
                  </td>
                  <td className="py-4 px-6 text-right font-mono text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    {log.ipAddress}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
